import { useState, type FormEvent } from 'react';

import {
    Button,
    Drawer,
    DrawerFooter,
    DrawerHeader,
    Field,
    Input,
    StatusPill,
} from '@/design-system';
import { ROLE_SUMMARY } from '@/entities/user';
import { errorMessage, isApiError, ROLE_LABELS } from '@/shared/api';
import { formatDayShort } from '@/shared/lib';

import { inspectorPickerCopy } from './copy';
import { useInviteInspector } from './hooks';

export interface InviteInspectorPanelProps {
    open: boolean;
    onClose: () => void;
}

type FieldName = 'name' | 'email';

/**
 * "Inspecteur uitnodigen" vanuit de inspecteurskeuze: naam en e-mailadres, met de
 * rol inspecteur, via dezelfde uitnodiging als Team in Instellingen
 * (`POST /invitations`).
 *
 * Kiezen kan pas als de uitnodiging is geaccepteerd - de API wijst een keuring
 * alleen toe aan een actief lid - en dat zegt het paneel voordat je verstuurt en
 * nadat het gelukt is. Tot dan staat de collega uitgeschakeld in de keuze.
 */
export function InviteInspectorPanel({ open, onClose }: InviteInspectorPanelProps) {
    const copy = inspectorPickerCopy.invitePanel;

    return (
        <Drawer open={open} onClose={onClose} side="right" width={480} title={copy.title}>
            <DrawerHeader
                icon="send"
                title={copy.title}
                description={copy.description}
                onClose={onClose}
                closeLabel={copy.close}
            />
            {/* Een dichte Drawer rendert niets: elke keer openen begint met een leeg formulier. */}
            <InviteInspectorForm onClose={onClose} />
        </Drawer>
    );
}

function InviteInspectorForm({ onClose }: { onClose: () => void }) {
    const copy = inspectorPickerCopy.invitePanel;
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [missing, setMissing] = useState<Partial<Record<FieldName, string>>>({});
    const invite = useInviteInspector();

    const fieldError = (field: FieldName): string | undefined =>
        missing[field] ??
        (isApiError(invite.error) ? invite.error.firstValidationError(field) : undefined);
    const formError =
        invite.error && !fieldError('name') && !fieldError('email')
            ? errorMessage(invite.error)
            : null;

    function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const next: Partial<Record<FieldName, string>> = {
            ...(name.trim() === '' ? { name: copy.nameRequired } : {}),
            ...(email.trim() === '' ? { email: copy.emailRequired } : {}),
        };

        setMissing(next);

        if (next.name || next.email) {
            return;
        }

        invite.mutate({ name: name.trim(), email: email.trim() });
    }

    if (invite.isSuccess) {
        return (
            <>
                <div className="flex flex-col gap-2 p-5">
                    <p
                        role="status"
                        className="m-0 text-[14px] leading-[1.5] text-[var(--accent-dark)]"
                    >
                        {copy.sent(invite.data.email, formatDayShort(invite.data.expiresAt))}
                    </p>
                    <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-secondary)]">
                        {copy.sentNext(invite.data.name ?? invite.data.email)}
                    </p>
                </div>
                <DrawerFooter>
                    <Button variant="primary" size="md" onClick={onClose}>
                        {copy.finish}
                    </Button>
                </DrawerFooter>
            </>
        );
    }

    return (
        <form onSubmit={submit} noValidate className="flex flex-1 flex-col">
            <div className="flex flex-col gap-4 p-5">
                <Field label={copy.name} error={fieldError('name')}>
                    <Input
                        value={name}
                        autoComplete="off"
                        placeholder={copy.namePlaceholder}
                        onChange={(event) => setName(event.target.value)}
                    />
                </Field>

                <Field label={copy.email} error={fieldError('email')}>
                    <Input
                        type="email"
                        value={email}
                        autoComplete="off"
                        spellCheck={false}
                        placeholder={copy.emailPlaceholder}
                        onChange={(event) => setEmail(event.target.value)}
                    />
                </Field>

                <div className="flex flex-col gap-[5px]">
                    <span className="text-[12px] leading-[1.3] text-[var(--text-secondary)]">
                        {copy.role}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                        <StatusPill tone="info" size="sm">
                            {ROLE_LABELS.inspector}
                        </StatusPill>
                        <span className="text-[12px] leading-[1.4] text-[var(--text-secondary)]">
                            {ROLE_SUMMARY.inspector}
                        </span>
                    </div>
                </div>

                <p className="m-0 text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {copy.acceptFirst}
                </p>

                {formError ? (
                    <p role="alert" className="m-0 text-[13px] leading-[1.5] text-[var(--danger)]">
                        {formError}
                    </p>
                ) : null}
            </div>

            <DrawerFooter>
                <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    icon="send"
                    disabled={invite.isPending}
                >
                    {invite.isPending ? copy.busy : copy.submit}
                </Button>
                <Button variant="outline" size="md" onClick={onClose}>
                    {copy.cancel}
                </Button>
            </DrawerFooter>
        </form>
    );
}
